import { useEffect, useState } from 'react'
import { fetchLeaderboard, type LeaderboardData } from '../services/leaderboard.ts'
import { t, tx } from '../i18n/translations.ts'

interface Props {
  period?: 'daily' | 'weekly' | 'alltime'
  limit?: number
  /** 自分の順位を別枠で表示するか（ランキング画面等で true） */
  showMyEntry?: boolean
  /**
   * 値が変わるたびに強制的にリーダーボードを再フェッチさせるためのキー。
   * リザルト画面でスコア送信完了タイミングに合わせて bump される想定。
   * 省略時は period/limit 変化のみで再フェッチする従来挙動。
   */
  refreshKey?: number
  /** ランキング画面では一覧部分だけをスクロールさせる。 */
  scrollable?: boolean
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ok'; data: LeaderboardData }
  | { status: 'empty' }
  | { status: 'error'; message: string }

/**
 * 「みんなのナワバリ戦」タイムライン。
 *
 * スコア競争ではなく「他にもプレイしている人がいる」ことを伝えるための閲覧 UI。
 * 順位は補助的に表示し、主役はプレイヤーのアバター・名前・獲得スコア。
 *
 * `showMyEntry=true` を指定するとランキング上位とは別に自分の順位/スコアを表示する。
 * API の LeaderboardResponse.myEntry が null の場合は非表示。
 */
export function LeaderboardPanel({
  period = 'alltime',
  limit = 10,
  showMyEntry = false,
  refreshKey = 0,
  scrollable = false,
}: Props) {
  const [state, setState] = useState<LoadState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })
    fetchLeaderboard(period, limit)
      .then((data) => {
        if (cancelled) return
        if (!data || !data.entries || data.entries.length === 0) {
          setState({ status: 'empty' })
        } else {
          setState({ status: 'ok', data })
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : String(err)
        setState({ status: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [period, limit, refreshKey])

  return (
    <div className="timeline-panel">
      <h3 className="timeline-panel__title">🌍 {t('ranking')}</h3>

      {state.status === 'loading' && (
        <div className="timeline-panel__msg">{t('loading')}</div>
      )}

      {state.status === 'empty' && (
        <div className="timeline-panel__msg">
          {t('noScores')}
        </div>
      )}

      {state.status === 'error' && (
        <div className="timeline-panel__msg timeline-panel__msg--error">
          {t('loadFailed')}
        </div>
      )}

      {state.status === 'ok' && (
        <>
          <ul className={`timeline-panel__list ${scrollable ? 'timeline-panel__list--scrollable' : ''}`}>
            {state.data.entries.map((entry) => (
              <TimelineEntry
                key={`${entry.userId ?? 'anon'}-${entry.rank}`}
                entry={entry}
              />
            ))}
          </ul>
          {showMyEntry && state.data.myEntry && (
            <div className="timeline-panel__my-entry">
              <span className="timeline-panel__my-entry-label">{t('you')}</span>
              <span className="timeline-panel__my-entry-rank">
                {state.data.myEntry.rank}{t('rankSuffix')}
              </span>
              <span className="timeline-panel__my-entry-score">
                {state.data.myEntry.score.toLocaleString()}pt
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function TimelineEntry({ entry }: { entry: LeaderboardEntry }) {
  const avatar = resolveAvatar(entry)
  const rankBadge = rankBadgeFor(entry.rank)

  return (
    <li className="timeline-entry">
      <div className="timeline-entry__avatar" aria-hidden="true">
        {avatar.kind === 'image' ? (
          <img src={avatar.src} alt="" />
        ) : (
          <span className="timeline-entry__avatar-fallback">
            {avatar.text}
          </span>
        )}
      </div>
      <div className="timeline-entry__main">
        <div className="timeline-entry__name">{entry.displayName ?? t('player')}</div>
        <div className="timeline-entry__meta">
          {tx('earnedPoints', { score: entry.score.toLocaleString() })}
        </div>
      </div>
      {rankBadge && (
        <div className="timeline-entry__badge">{rankBadge}</div>
      )}
    </li>
  )
}

type Avatar =
  | { kind: 'image'; src: string }
  | { kind: 'text'; text: string }

function resolveAvatar(entry: LeaderboardEntry): Avatar {
  if (entry.pictureUrl) {
    return { kind: 'image', src: entry.pictureUrl }
  }
  // 名前の先頭1文字（絵文字含む）をフォールバックとして使用
  const name = (entry.displayName ?? '').trim()
  const firstChar = Array.from(name)[0] ?? '🐾'
  return { kind: 'text', text: firstChar }
}

function rankBadgeFor(rank: number): string | null {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return null
}
